<!-- @submit.prevent = prevent default action (server upload, page reload) -->

<template>
  <form ref="formRef" :class="{loading: loading}" @submit.prevent="onSubmit">
    <slot />
  </form>
</template>

<script setup>
const log = useLog(import.meta); // switch to 'useLog()' to disable logging
const formRef = ref(null);
const props = defineProps({loading: Boolean, autofocus: Object});
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const emit = defineEmits(["submit"]);

async function trySetFocus(element, retries = 25) {
  if (!element) return;
  element.focus();
  if (document.activeElement === element) return element.select();
  if (retries <= 0) return;
  await sleep(20);
  await trySetFocus(element, retries - 1);
}

function getLabels(formRef) {
  if (!formRef?.value) return [];
  const labelRefs = Array.from(formRef.value.querySelectorAll("label"));
  const output = [];
  for (const labelRef of labelRefs) {
    const forValue = labelRef?.getAttribute("for");
    // if (!forValue) continue;
    const labelValue = labelRef?.textContent.trim();
    output.push({labelRef, forValue, labelValue});
  }
  return output;
}

function setInputValue({labelRef, labelValue, forValue}) {
  log(`set value in <input> element, if not already set`);
  const inputRef = labelRef.querySelector("input[type='radio']"); // select child radio <input>
  if (!inputRef) return; // Early exit if no child <input> is found
  inputRef.value ||= labelValue; // logical 'OR' assignment
  // assign only if the existing value is falsy
}

function setButtonNames({labelRef, labelValue, forValue}) {
  // Assigns "name" values to all <button> elements based on the associated <label>
  // The association is determined by <button> class names matching the <label> 'for'
  const matchingNodes = formRef.value.querySelectorAll(`.${forValue}`);
  const elementArray = Array.from(matchingNodes);
  const buttons = elementArray.filter((el) => el.tagName === "BUTTON");
  buttons.forEach((button) => (button.name ||= labelValue));
}

function setupFormElements() {
  log(`running pre-processing setup to modify form elements`);
  if (!formRef?.value) return;
  log(`formRef?.value is truthy`);
  const labels = getLabels(formRef);
  for (const label of labels) {
    log(`labelRef = ${label.labelRef instanceof HTMLElement}`);
    log(`labelRef = ${label.labelRef?.querySelector("input")}`);

    if (label.labelRef?.querySelector("input")) {
      setInputValue(label); // select <label> elements with child <input> element
    } else {
      setButtonNames(label);
    }
  }
  const fieldsets = formRef.value.querySelectorAll("fieldset");
  log(`we got through setupFormElements()`);
  if (!fieldsets.length) return;
  // for (const fieldset of fieldsets) setupFieldSet(fieldset);
}

function captureFieldSet(fieldset) {
  // On submission, captures selected radio button value
  // Handles if the "Other" input if selected.
  const selectedRadio = fieldset.querySelector('input[type="radio"]:checked');
  if (!selectedRadio) return;
  let selectedValue = selectedRadio.parentElement.textContent.trim();
  const otherInput = document.getElementById("other-input");
  if (!otherInput) return;
  const otherInputValue = otherInput.value.trim();
  const isOtherSelected = selectedRadio.id === "other" && otherInputValue;
  fieldset.dataset.finalValue = isOtherSelected ? otherInputValue : selectedValue;
}

function onSubmit(event) {
  event.preventDefault();
  const fieldsets = formRef.value.querySelectorAll("fieldset");
  for (const fieldset of fieldsets) captureFieldSet(fieldset);
  emit("submit", event); // Forward the event to the parent
}

onMounted(async () => {
  log(`onMounted()`); // returns after error
  await nextTick();
  setupFormElements(); // returns before error
  await nextTick();
  if (!formRef?.value) return;
  await nextTick();
  log(` formRef.value is truthy`); // returns after error
  if (props.autofocus?.value) {
    if ((await trySetFocus(props.autofocus.value)) == null) log("autofocus failed");
    return;
  }
  log(` props.autofocus?.value is falsy`);
  const fallback = formRef.value.querySelector("input, textarea, select");
  if (fallback) {
    if ((await trySetFocus(fallback)) == null) log("fallback focus failed");
  }
  log(`finished onMounted()`);
});
</script>

<style scoped>
form {
  display: flex;
  flex-direction: column;
  gap: var(--space-x-small); /* Keeps consistent spacing */
  width: 100%;
  /* background-color: red; */
}
body:not(.narrow-width) {
  padding: var(--space-large) var(--space-x-large);
}
form.loading {
  opacity: 0.5; /* Simple loading state styling */
  pointer-events: none;
}
:deep(textarea) {
  width: 100%;
  height: calc(2.5 * var(--line-height));
  resize: none;
}

:deep(:is(textarea, input, select, label)) {
  padding: 0 !important; /* needed for  'other' <input> */
  font-size: var(--font-size-x-small) !important; /* needed for  'other' <input> */
  margin-bottom: calc(var(--space-medium));

  /* font-family: "Roboto Mono", monospace; */
}

:deep(
  input:is(
    [type="text"],
    [type="email"],
    [type="password"],
    [type="url"],
    [type="search"],
    [type="tel"],
    [type="number"]
  )
) {
  width: 100%;
}

:deep(:is(h2, h3)) {
  font-size: var(--font-size-x-large);
  padding-bottom: var(--space-small);
}

:deep(label) {
  display: block;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0;
}

:deep(:is(label, fieldset legend)) {
  font-weight: 400;
  margin-bottom: calc(var(--space-xx-small) / 4);
  padding: calc(var(--space-xx-small) / 4);
  color: var(--base-black-soft);
}

:deep(fieldset) {
  border: none;
}

:deep(fieldset > label) {
  /* gap: 0.125rem; */
  gap: var(--space-xx-small);
}

/* Radio Button Styling */
:deep(input[type="radio"]) {
  /* all: unset; */
  all: revert; /* Resets inherited styles but keeps native behavior */
  appearance: none; /* Removes native radio button UI */
  position: relative;
  display: inline-block;
  /*vertical-align: middle; /* Aligns with text */
  width: 1rem;
  height: 1rem;
  line-height: var(--line-height, 1.2); /* Ensures proper height */
  border: 2px solid var(--color-border, #b0b0b0);
  border-radius: 50%;
  outline: none;
  /* transition: border-color 0.3s, background-color 0.3s; */
}

/* Inner circle when checked */
:deep(input[type="radio"]:checked) {
  border-color: var(--brand-color);
  background-color: var(--brand-color);
}

:deep(input[type="radio"]::after) {
  content: "";
  width: 0.5rem;
  height: 0.5rem;
  background-color: var(--brand-color-alt);
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  /* transition: transform 0.2s ease-in-out; */
}

:deep(input[type="radio"]:checked::after) {
  transform: translate(-50%, -50%) scale(1); /* inner circle */
}

/* Label hover effect */
:deep(label:hover:has(input[type="radio"])) {
  /* color: var(--brand-color-alt); */
  background-color: rgba(190, 190, 190, 0.05); /* Light gray tint */
  border-radius: var(--border-radius-small); /* Slight rounding for smooth feel */
  /* transition: background-color 0.2s ease-in-out; Smooth Apple-like transition */
}

:deep(label:has(input[type="radio"])) {
  display: flex;
  align-items: center;
  gap: var(--space-x-small);
  margin-bottom: 0rem;
  font-weight: normal;
  font-size: var(--font-size-small);
  cursor: pointer;
}

/* Label highlight when radio is checked */
:deep(input[type="radio"]:checked + label) {
  color: var(--brand-color-alt);
}

:deep(input) {
  padding: var(--space-xx-small) !important;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--space-small);
  width: 100%;
  /* color: var(--color); */
  color: var(--base-black);
}
:deep(input:focus) {
  border-color: var(--brand-color);
  outline: none;
}
:deep(button) {
  padding: var(--space-xx-small) var(--space-large);
  font-weight: 600;
  background-color: var(--brand-color);
  /* color: #fff; */
  border: none;
  border-radius: var(--border-radius-medium);
  cursor: pointer;
  width: 100%;
  /* transition: background-color 0.3s; */
}
:deep(button:hover) {
  filter: brightness(1.1) saturate(1.2); /* background-color: var(--brand-color-alt); */
}
:deep(button:disabled) {
  background-color: #ccc;
  color: #939393;
  cursor: not-allowed;
}
:deep(button:disabled:hover) {
  background-color: #ccc;
}
:deep(select) {
  appearance: none;
  width: 100%;
  /* background-color: var(--background-color); */
  padding: var(--space-xx-small) !important;
  margin-bottom: var(--space-small);

  border: 1px solid var(--color-border, #ccc);
  border-radius: var(--border-radius-small);
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 20 20' fill='none' stroke='black' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 8l4 4 4-4' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--space-small) center;
  background-size: 1rem;
}
:deep(select:focus) {
  /* Remove the inner focus ring (optional) */
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5); /* similar to focus:ring */
  border-color: #2563eb; /* Tailwind blue-600 */
}

/* Add border between elements */
/* form > * { */
/* border-bottom: 1px solid var(--color-border, #ccc); */
/* padding-bottom: calc(var(--space-small)); */
/* margin-bottom: calc(var(--space-small) / 2); */
/* } */
/* Remove border from last element */
:is(form > *:last-child) {
  border-bottom: none;
}
</style>
